<script setup lang="ts">
  import { useSlots } from 'vue';

  /**
   * One card of the editor's body (plan §5.12): a white card with a title,
   * an optional action at the right of it, and the content. `flush` lets
   * rows draw their own hairlines edge to edge.
   */
  withDefaults(
    defineProps<{
      title: string;
      /** A short explanation under the title. */
      description?: string;
      flush?: boolean;
    }>(),
    { description: undefined, flush: false }
  );

  const slots = useSlots();
</script>

<template>
  <section class="v2-catalog-editor-section" :class="{ 'is-flush': flush }">
    <header class="section-header">
      <div class="section-heading">
        <h3 class="section-title">{{ title }}</h3>
        <p v-if="description" class="section-description">
          {{ description }}
        </p>
      </div>
      <div v-if="slots.actions" class="section-actions">
        <slot name="actions" />
      </div>
    </header>
    <div class="section-body"><slot /></div>
  </section>
</template>

<style scoped lang="scss">
  .v2-catalog-editor-section {
    @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface;

    & + .v2-catalog-editor-section {
      @apply tw-mt-3;
    }

    .section-header {
      @apply tw-flex tw-items-start tw-px-4 tw-pt-4;
    }

    .section-heading {
      @apply tw-min-w-0 tw-flex-1;
    }

    .section-title {
      @apply tw-m-0 tw-text-[15px] tw-font-semibold tw-leading-6 tw-text-neutral-900;
    }

    .section-description {
      @apply tw-mb-0 tw-mt-0.5 tw-text-sm tw-leading-relaxed tw-text-neutral-500;
    }

    .section-actions {
      @apply -tw-mr-2 -tw-mt-2 tw-ml-2 tw-flex tw-flex-none tw-items-center;
    }

    .section-body {
      @apply tw-px-4 tw-pb-4 tw-pt-3;
    }

    &.is-flush .section-body {
      @apply tw-px-0 tw-pb-1;
    }
  }
</style>
